import React, { useState } from 'react';
import type { EpicDetail, EpicSession } from '../../../../../../shared/types/epics';
import { EpicPipeline } from '../../../../components/epicDetail/EpicPipeline';
import { DiagnosticsPanel } from '../../../../components/epicDetail/DiagnosticsPanel';
import { StoriesAndSessions } from '../stories/StoriesAndSessions';
import { OrphanSessionsCard } from '../stories/OrphanSessionsCard';
import { ResolvedConfigCard } from '../cost/ResolvedConfigCard';

const DEBUG_STORAGE_KEY = 'epic-debug-open';

/** sessionStorage read/write wrapped in try/catch — private browsing / blocked
 *  site data must not break the page, it should just fall back to "closed". */
function readDebugOpen(): boolean {
  try {
    return window.sessionStorage.getItem(DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDebugOpen(open: boolean): void {
  try {
    if (open) window.sessionStorage.setItem(DEBUG_STORAGE_KEY, '1');
    else window.sessionStorage.removeItem(DEBUG_STORAGE_KEY);
  } catch {
    // ignore — private window / blocked storage
  }
}

export const ProgressTab: React.FC<{
  epic: EpicDetail;
  epicId: string;
  sessionsByStory: Map<string, EpicSession[]>;
  orphanSessions: EpicSession[];
  paused: boolean;
  onTogglePaused: () => void;
  onOpenTranscript: (storyId: string, agentType: string, taskArn?: string | null) => void;
}> = ({ epic, epicId, sessionsByStory, orphanSessions, paused, onTogglePaused, onOpenTranscript }) => {
  // Closed by default; remembered per browser tab across tab switches and
  // page loads (UX-05 §6) — not React state, since this component unmounts
  // whenever the operator switches to another tab.
  const [debugOpen, setDebugOpen] = useState(readDebugOpen);

  return (
    <div className="space-y-5">
      <EpicPipeline
        epic={epic}
        onOpenTranscript={(storyId, agentType) => onOpenTranscript(storyId, agentType)}
        paused={paused}
        onTogglePaused={onTogglePaused}
      />
      <StoriesAndSessions
        epic={epic}
        sessionsByStory={sessionsByStory}
        orphanSessions={[]}
        onOpenTranscript={onOpenTranscript}
      />
      {orphanSessions.length > 0 && (
        <OrphanSessionsCard sessions={orphanSessions} onOpenTranscript={onOpenTranscript} />
      )}
      <details
        data-testid="epic-debug"
        open={debugOpen}
        onToggle={(e) => {
          const open = e.currentTarget.open;
          setDebugOpen(open);
          writeDebugOpen(open);
        }}
        className="rounded-lg border border-slate-200 bg-white"
      >
        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-slate-700">
          Debug
        </summary>
        <div className="space-y-4 px-3 pb-3 pt-1">
          {/* Lazy-loaded: only mounts (and fetches) once Debug is open. */}
          {debugOpen && <DiagnosticsPanel epicId={epicId} />}
          {epic.resolvedConfig && <ResolvedConfigCard config={epic.resolvedConfig} />}
        </div>
      </details>
    </div>
  );
};
